import * as React from "react";
import { ArrowRight } from "lucide-react";
import {
  Checkbox,
  cn,
  Column,
  type ColumnConfigState,
  DataTable,
  Hyperlink,
  StatusPill,
} from "@racoon/ui";
import { IcDelete, IcDragAndDrop } from "@racoon/icons";
import { Namespace, TranslationKey, useT } from "@racoon/i18n";
import {
  jobOutcome,
  nextResolution,
  resolutionOf,
  type JobResolution,
  type Resolutions,
} from "~/features/force-complete-shift/lib/force-complete-shift";
import { isFinalJobStatus } from "~/shared/lib/job-status";
import { PhotoReportModal } from "~/features/photo-report-modal/ui/photo-report.modal";
import { plannedVolume } from "~/features/photo-report-modal/model/container-volume";
import {
  REORDERABLE_JOB_STATUSES,
  REORDERABLE_ROUTE_STATUS,
  SELECTION_DISABLED_ROUTE_STATUSES,
} from "./msw-route-list.types";
import { badge } from "~/shared/helpers";
import { JobStatusPill } from "./status-pills";
import { splitCheckedJobs } from "./selected-jobs-action";
import { SYNC_SCROLL_X, SYNC_SCROLL_X_HEADER } from "./use-synced-scroll-x";
import { useDateFnsLocale } from "@racoon/i18n";
import { format } from "date-fns";
import { ConnectProto } from "@racoon/api";
import {
  JOB_STATUS_TONE,
  STATUS_TONE_CLASS,
  fromTimestamp,
  getEnumName,
  sortJobsByStep,
} from "@racoon/lib";
import {
  formatVolume,
  getJobFactContainerCount,
  getJobFactVolume,
} from "~/shared/lib/job-metrics";
import { plannedContainerCount } from "~/shared/lib/job-containers";

// Shared by both variants so the route list and the TTN render the group alike.
const ContainerGroupCell: React.FC<{ job: ConnectProto.Job }> = ({ job }) => (
  <span className="flex items-center justify-center gap-2 whitespace-nowrap">
    <span className="text-text-body">{job.containerGroup?.containerType?.name ?? "-"}</span>
    {plannedContainerCount(job) ? (
      <span className="text-[#667489]">({plannedContainerCount(job)})</span>
    ) : null}
  </span>
);

const PhotoReportCell: React.FC<{ job: ConnectProto.Job; jobs: ConnectProto.Job[] }> = ({
  job,
  jobs,
}) => {
  return <PhotoReportModal job={job} getJobs={() => jobs} />;
};

type Props = {
  /** The trip's status; absent on the shared header, which stands for no one trip. */
  routeStatus?: ConnectProto.RouteStatus;
  jobs: ConnectProto.Job[];
  selectedIds: Set<bigint>;
  onSelectionChange: (selectedIds: Set<bigint>) => void;
  /** Per-row "remove from route". */
  onRemoveJob?: (jobId: bigint) => void;
  /** The remove column; by default there when `onRemoveJob` is. The shared header sets it. */
  showRemoveColumn?: boolean;
  isRemoving?: boolean;
  onJobsReorder?: (reorderedJobIds: bigint[]) => void;
  /** The cards owning the site and the customer named on each row. The cascade
   *  holds them, so the sheet's routes share one of each rather than one per table. */
  onLocationClick?: (location: ConnectProto.PickupLocation) => void;
  onCustomerClick?: (customer: ConnectProto.Organization) => void;
  focusedJobId?: bigint | null;
  /** Set when the job was picked on the map, so its row is brought into view. */
  scrollToFocusedJob?: boolean;
  /** Row click: the map flies to this job. */
  onJobFocus?: (jobId: bigint) => void;
  /** Opens the job's create/edit card; only offered while the job is still editable. */
  onJobClick?: (job: ConnectProto.Job) => void;
  showSelection?: boolean;
  /**
   * "ttn": the TTN page's completed jobs (Figma 21344:42369) — nominal vs actual volume, no reordering.
   * "resolution": the emergency shift completion (Figma 21279:43447) — each unfinished job's
   * current status next to what it becomes, a checkbox on the unfinished ones, no reordering.
   */
  variant?: "default" | "ttn" | "resolution";
  /** "resolution" only: what each unfinished job becomes; absent means unassigned. */
  resolutions?: Resolutions;
  /** "resolution" only: the pill was clicked — the job moves to the next resolution. */
  onResolutionChange?: (jobId: bigint, resolution: JobResolution) => void;
  /**
   * The route sheet draws one header over all its trips (`MswRouteListTableHeader`),
   * so each trip's table keeps its columns but hides its own.
   */
  headerMode?: "default" | "hidden" | "only";
  /** Shared by the header and every trip, so a column resized in the header resizes them all. */
  columnConfig?: ColumnConfigState;
  onColumnConfigChange?: (config: ColumnConfigState) => void;
};
const isRouteReorderEnabled = (routeStatus: ConnectProto.RouteStatus) =>
  REORDERABLE_ROUTE_STATUS.includes(routeStatus);

const isJobReorderEnabled = (status: ConnectProto.JobStatus) =>
  REORDERABLE_JOB_STATUSES.includes(status);

const isSelectionDisabled = (routeStatus: ConnectProto.RouteStatus) =>
  SELECTION_DISABLED_ROUTE_STATUSES.includes(routeStatus);

const EMPTY_RESOLUTIONS: Resolutions = new Map();

const jobStatusKey = (status: ConnectProto.JobStatus) =>
  getEnumName(ConnectProto.JobStatusSchema, status) as TranslationKey<Namespace.Enum>;

/** The platform's job-status colours, for a status or for what a resolution makes of the job. */
const jobTone = (status: ConnectProto.JobStatus) => {
  const tone = STATUS_TONE_CLASS[JOB_STATUS_TONE[jobStatusKey(status)]];
  return tone ? `${tone.bg} ${tone.color}` : undefined;
};

export const MswRouteListTable: React.FC<Props> = ({
  routeStatus,
  jobs,
  selectedIds,
  onSelectionChange,
  onJobsReorder,
  onLocationClick,
  onCustomerClick,
  onJobClick,
  onRemoveJob,
  showRemoveColumn = Boolean(onRemoveJob),
  isRemoving = false,
  focusedJobId,
  scrollToFocusedJob = false,
  onJobFocus,
  showSelection = true,
  variant = "default",
  resolutions = EMPTY_RESOLUTIONS,
  onResolutionChange,
  headerMode = "default",
  columnConfig,
  onColumnConfigChange,
}) => {
  const t = useT();
  const focusedRowKeys = React.useMemo(
    () => (focusedJobId != null ? [String(focusedJobId)] : []),
    [focusedJobId],
  );
  const dateFnsLocale = useDateFnsLocale();
  const isTtn = variant === "ttn";
  const isResolution = variant === "resolution";
  const routeReorderable = routeStatus !== undefined && isRouteReorderEnabled(routeStatus);
  const reorderEnabled = !isTtn && !isResolution && routeReorderable && !isRemoving;
  const selectionDisabled = routeStatus !== undefined && isSelectionDisabled(routeStatus);

  const checkedRowKeys = React.useMemo(
    () => Array.from(selectedIds, (id) => String(id)),
    [selectedIds],
  );

  const handleCheckedRowKeysChange = React.useCallback(
    (keys: React.Key[]) => {
      onSelectionChange(new Set(keys.map((key) => BigInt(key))));
    },
    [onSelectionChange],
  );

  // Jobs come from the API in arbitrary order — the route step order is carried
  // by stepIdx, so both the "#" column and the row order follow it.
  const sortedJobs = React.useMemo(() => sortJobsByStep(jobs), [jobs]);

  const ttnColumns = React.useMemo<Column<ConnectProto.Job>[]>(
    () => [
      {
        id: "order",
        header: "#",
        accessor: (job, index) => Number(job.stepIdx ?? 0) || index + 1,
        width: 36,
        sortable: false,
      },
      {
        id: "pointId",
        header: "ID",
        accessor: (job) => `Z${String(job.id)}`,
        width: 77,
        sortable: false,
        align: "center" as const,
        cell: ({ row }) => {
          const job = row.original;
          const label = `Z${String(job.id)}`;
          if (!onJobClick) return label;
          return <Hyperlink onOpen={() => onJobClick(job)}>{label}</Hyperlink>;
        },
      },
      {
        id: "address",
        header: t(Namespace.Routes, "object"),
        accessor: (job) => job.pickupLocation?.name ?? "-",
        sortable: false,
        cell: ({ row }) => {
          const location = row.original.pickupLocation;
          if (!location?.name || !onLocationClick) return location?.name ?? "-";
          return <Hyperlink onOpen={() => onLocationClick(location)}>{location.name}</Hyperlink>;
        },
      },
      {
        id: "customer",
        header: t(Namespace.Routes, "colCustomer"),
        accessor: (job) => job.serviceReceiver?.name ?? "-",
        width: 160,
        sortable: false,
        cell: ({ row }) => {
          const customer = row.original.serviceReceiver;
          if (!customer?.name || !onCustomerClick)
            return <span className="leading-5">{customer?.name ?? "-"}</span>;
          return (
            <Hyperlink className="leading-5" onOpen={() => onCustomerClick(customer)}>
              {customer.name}
            </Hyperlink>
          );
        },
      },
      {
        id: "containerGroup",
        header: t(Namespace.Routes, "colContainerGroup"),
        accessor: (job) => job.containerGroup?.containerType?.name ?? "-",
        width: 127,
        sortable: false,
        align: "center" as const,
        cell: ({ row }) => <ContainerGroupCell job={row.original} />,
      },
      {
        id: "nominalVolume",
        header: t(Namespace.Routes, "ttnColNominalVolume"),
        accessor: (job) => formatVolume(plannedVolume(job)),
        width: 80,
        sortable: false,
        align: "center" as const,
      },
      // Next to the fact volume: the two facts read together.
      {
        id: "factCount",
        header: t(Namespace.Routes, "ttnColFactCount"),
        accessor: (job) => getJobFactContainerCount(job) ?? "-",
        width: 64,
        sortable: false,
        align: "center" as const,
      },
      {
        id: "factVolume",
        header: t(Namespace.Routes, "ttnColFactVolume"),
        accessor: (job) => formatVolume(getJobFactVolume(job)),
        width: 80,
        sortable: false,
        align: "center" as const,
      },
      {
        id: "hasPhotoReport",
        header: t(Namespace.Orders, "columns.photo"),
        accessor: (job) => job,
        width: 65,
        sortable: false,
        align: "center" as const,
        cell: ({ row }) => <PhotoReportCell job={row.original} jobs={sortedJobs} />,
      },
    ],
    [t, sortedJobs, onLocationClick, onCustomerClick, onJobClick],
  );

  const defaultColumns = React.useMemo<Column<ConnectProto.Job>[]>(
    () => [
      {
        id: "order",
        header: "#",
        accessor: (job, index) => Number(job.stepIdx ?? 0) || index + 1,
        width: 48,
        sortable: false,
      },
      {
        id: "pointId",
        header: "ID",
        accessor: (job) => `Z${String(job.id)}`,
        width: 100,
        sortable: false,
        cell: ({ row }) => {
          const canDrag =
            routeReorderable && isJobReorderEnabled(row.original.status) && !isRemoving;

          return (
            <div className="flex items-center gap-1">
              <span
                className={
                  canDrag
                    ? "cursor-grab active:cursor-grabbing text-muted-foreground"
                    : "text-muted-foreground/40 cursor-default"
                }
              >
                <IcDragAndDrop className="h-6 w-6 text-ds-icon-default" />
              </span>
              {onJobClick ? (
                <Hyperlink onOpen={() => onJobClick(row.original)}>
                  {`Z${String(row.original.id)}`}
                </Hyperlink>
              ) : (
                <span>{`Z${String(row.original.id)}`}</span>
              )}
            </div>
          );
        },
      },
      {
        id: "statusLabel",
        header: t(Namespace.Routes, "colStatus"),
        accessor: (job) => job.status,
        width: 134,
        sortable: false,
        cell: ({ row }) => <JobStatusPill status={row.original.status} />,
      },
      {
        id: "address",
        header: t(Namespace.Routes, "object"),
        accessor: (job) => job.pickupLocation?.name ?? "-",
        width: 240,
        sortable: false,
        cell: ({ row }) => {
          const location = row.original.pickupLocation;
          if (!location?.name || !onLocationClick) return location?.name ?? "-";
          return <Hyperlink onOpen={() => onLocationClick(location)}>{location.name}</Hyperlink>;
        },
      },
      {
        id: "customer",
        header: t(Namespace.Routes, "colCustomer"),
        accessor: (job) => job.serviceReceiver?.name ?? "-",
        width: 240,
        sortable: false,
        cell: ({ row }) => {
          const customer = row.original.serviceReceiver;
          if (!customer?.name || !onCustomerClick) return customer?.name ?? "-";
          return <Hyperlink onOpen={() => onCustomerClick(customer)}>{customer.name}</Hyperlink>;
        },
      },
      {
        id: "containerGroup",
        header: t(Namespace.Routes, "colContainerGroup"),
        accessor: (job) => job.containerGroup?.containerType?.name ?? "-",
        width: 127,
        sortable: false,
        align: "center" as const,
        cell: ({ row }) => <ContainerGroupCell job={row.original} />,
      },
      {
        id: "nominalVolume",
        header: t(Namespace.Routes, "ttnColNominalVolume"),
        accessor: (job) => formatVolume(plannedVolume(job)),
        width: 80,
        sortable: false,
        align: "center" as const,
      },
      {
        id: "factCount",
        header: t(Namespace.Routes, "ttnColFactCount"),
        accessor: (job) => getJobFactContainerCount(job) ?? "-",
        width: 64,
        sortable: false,
        align: "center" as const,
      },
      {
        id: "factVolume",
        header: t(Namespace.Routes, "ttnColFactVolume"),
        accessor: (job) => formatVolume(getJobFactVolume(job)),
        width: 80,
        sortable: false,
        align: "center" as const,
      },
      {
        id: "doneAt",
        header: t(Namespace.Routes, "colDoneAt"),
        accessor: (job) => fromTimestamp(job.factEndAt),
        width: 117,
        sortable: false,
        cell: ({ getValue }) => {
          const date = getValue<Date | null>();
          if (!date) return "-";
          return (
            <div className="flex flex-col text-xs leading-4">
              <span>{format(date, "HH:mm", { locale: dateFnsLocale })}</span>
              <span>{format(date, "dd MMM yyyy", { locale: dateFnsLocale })}</span>
            </div>
          );
        },
      },
      {
        id: "hasPhotoReport",
        header: t(Namespace.Routes, "colReport"),
        accessor: (job) => job,
        width: 78,
        sortable: false,
        align: "center" as const,
        cell: ({ row }) => {
          return <PhotoReportCell job={row.original} jobs={sortedJobs} />;
        },
      },
      // Rendered unconditionally per row (DataTable memoizes rows); parity with
      // the route card, where every job carries its own remove action.
      // Present on every trip, so all trips share one set of columns (and one header);
      // a trip whose jobs can't be removed shows it disabled.
      ...(showRemoveColumn
        ? [
            {
              id: "actions",
              header: "",
              accessor: (job: ConnectProto.Job) => job,
              width: 48,
              sortable: false,
              align: "center" as const,
              cell: ({ row }: { row: { original: ConnectProto.Job } }) => {
                const disabled =
                  !onRemoveJob ||
                  isRemoving ||
                  selectionDisabled ||
                  isFinalJobStatus(row.original.status);
                return (
                  <button
                    type="button"
                    title={t(Namespace.Routes, "removeJob")}
                    disabled={disabled}
                    onClick={(e) => {
                      e.stopPropagation();
                      if (!disabled) onRemoveJob?.(row.original.id);
                    }}
                    className={
                      disabled
                        ? "text-text-secondary/40 cursor-not-allowed"
                        : "text-text-secondary hover:text-text-primary"
                    }
                  >
                    <IcDelete className="h-5 w-5" />
                  </button>
                );
              },
            } as Column<ConnectProto.Job>,
          ]
        : []),
    ],
    [
      t,
      dateFnsLocale,
      routeReorderable,
      onLocationClick,
      onCustomerClick,
      onJobClick,
      onRemoveJob,
      showRemoveColumn,
      selectionDisabled,
      isRemoving,
      sortedJobs,
    ],
  );
  // The jobs the completion can still decide about; final ones only show their status.
  const unfinishedJobs = React.useMemo(
    () => sortedJobs.filter((job) => !isFinalJobStatus(job.status)),
    [sortedJobs],
  );

  const resolutionColumns = React.useMemo<Column<ConnectProto.Job>[]>(() => {
    const shared = new Map(ttnColumns.map((column) => [column.id, column]));
    const pick = (id: string) => shared.get(id) as Column<ConnectProto.Job>;
    const allChecked =
      unfinishedJobs.length > 0 && unfinishedJobs.every((job) => selectedIds.has(job.id));
    // One set for the header's check-all and a row's own box alike.
    const toggle = (jobs: ConnectProto.Job[], checked: boolean) => {
      const next = new Set(selectedIds);
      jobs.forEach((job) => (checked ? next.add(job.id) : next.delete(job.id)));
      onSelectionChange(next);
    };

    return [
      {
        id: "select",
        header: () => (
          <Checkbox
            aria-label="Выбрать все строки"
            data-testid="force-complete-shift-job-check-all"
            disabled={unfinishedJobs.length === 0}
            checked={allChecked}
            onCheckedChange={(checked) => toggle(unfinishedJobs, checked === true)}
          />
        ),
        accessor: () => null,
        width: 40,
        sortable: false,
        align: "center" as const,
        cell: ({ row }) => (
          <Checkbox
            data-testid={`force-complete-shift-job-check-${String(row.original.id)}`}
            disabled={isFinalJobStatus(row.original.status)}
            checked={selectedIds.has(row.original.id)}
            onCheckedChange={(checked) => toggle([row.original], checked === true)}
          />
        ),
      },
      pick("order"),
      pick("pointId"),
      { ...pick("address"), header: t(Namespace.Routes, "colAddress") },
      pick("customer"),
      {
        ...pick("containerGroup"),
        header: t(Namespace.ForceCompleteShift, "column.containerGroup"),
      },
      {
        id: "statusChange",
        header: t(Namespace.ForceCompleteShift, "column.statusChange"),
        accessor: (job) => job.status,
        width: 280,
        sortable: false,
        cell: ({ row }) => {
          const job = row.original;
          const statusKey = jobStatusKey(job.status);
          // A settled job shows one pill across the column.
          if (isFinalJobStatus(job.status)) {
            return (
              <StatusPill tone={jobTone(job.status)} className="w-full">
                {t(Namespace.Enum, statusKey)}
              </StatusPill>
            );
          }
          const current = badge(t(Namespace.Enum, statusKey), statusKey);

          const resolution = resolutionOf(resolutions, job.id);
          return (
            <span className="flex items-center gap-2">
              {current}
              <ArrowRight className="size-4 shrink-0 text-icon-secondary" />
              <button
                type="button"
                data-testid={`force-complete-shift-resolution-${String(job.id)}`}
                data-resolution={resolution}
                onClick={(event) => {
                  event.stopPropagation();
                  onResolutionChange?.(job.id, nextResolution(resolution, job.status));
                }}
                className="rounded-full transition-opacity hover:opacity-90"
              >
                <StatusPill tone={jobTone(jobOutcome(resolution))} className="min-w-[7rem]">
                  {t(Namespace.Enum, jobStatusKey(jobOutcome(resolution)))}
                </StatusPill>
              </button>
            </span>
          );
        },
      },
    ];
  }, [
    t,
    ttnColumns,
    unfinishedJobs,
    selectedIds,
    onSelectionChange,
    resolutions,
    onResolutionChange,
  ]);

  const columns = isTtn ? ttnColumns : isResolution ? resolutionColumns : defaultColumns;

  return (
    <div className="w-full">
      <DataTable
        id="msw-route-list-table"
        columns={columns}
        data={sortedJobs}
        className={cn(
          SYNC_SCROLL_X,
          headerMode === "hidden" ? "w-full !rounded-none !border-0" : "w-full !rounded-lg border",
          // The shared header follows the trips' scroll; the scrollbar belongs to the tables.
          headerMode === "only" &&
            cn(SYNC_SCROLL_X_HEADER, "[scrollbar-width:none] [&::-webkit-scrollbar]:hidden"),
        )}
        stickyHeader={false}
        zebra={!isTtn}
        density="compact"
        rowKey={(row) => String(row.id)}
        selectable={!!onJobFocus}
        selectedRowKeys={focusedRowKeys}
        // A row clicked in the table is already in view; centring it would slide it
        // from under the cursor.
        autoScrollToSelected={scrollToFocusedJob}
        onRowClick={(job) => onJobFocus?.(job.id)}
        rowClassName={(job) =>
          focusedJobId != null && job.id === focusedJobId
            ? "!bg-brand-blue-light"
            : isResolution && isFinalJobStatus(job.status)
              ? // Dimmed as settled, except its checkbox (dimmed by being disabled) and status.
                "[&>td:not(:first-child):not(:last-child)]:opacity-50"
              : ""
        }
        checkable={!isResolution && showSelection}
        // Disabled rather than dropped, so the column (and the shared header) stays.
        checkDisabled={selectionDisabled}
        headerMode={headerMode}
        columnConfig={columnConfig}
        onColumnConfigChange={onColumnConfigChange}
        checkedRowKeys={checkedRowKeys}
        onCheckedRowKeysChange={handleCheckedRowKeysChange}
        enableColumnReordering={headerMode === "only"}
        enableRowDrag={reorderEnabled}
        dragColumnId={reorderEnabled ? "pointId" : undefined}
        onRowDragEnd={(draggedRows) => {
          onJobsReorder?.(draggedRows.map((row) => row.id));

          const newSelectedIds = new Set(
            draggedRows.filter((row) => selectedIds.has(row.id)).map((row) => row.id),
          );
          onSelectionChange(newSelectedIds);
        }}
      />
    </div>
  );
};

/**
 * The route sheet's one header over all its trips (Figma "Table Column Headers"): the
 * same columns as each trip's table, its check-all box acting on every trip at once.
 */
export const MswRouteListTableHeader: React.FC<{
  routes: ConnectProto.Route[];
  selection: Map<bigint, Set<bigint>>;
  onSelectionChange: (routeId: bigint, selectedIds: Set<bigint>) => void;
  showSelection?: boolean;
  withRemove?: boolean;
  variant?: "default" | "ttn";
  columnConfig?: ColumnConfigState;
  onColumnConfigChange?: (config: ColumnConfigState) => void;
}> = ({ routes, selection, onSelectionChange, withRemove, ...rest }) => {
  // Check-all covers the trips whose jobs can be picked at all.
  const pickable = routes.filter((route) => !isSelectionDisabled(route.status));
  const jobs = pickable.flatMap((route) => route.jobs);
  const selected = new Set(pickable.flatMap((route) => [...(selection.get(route.id) ?? [])]));
  return (
    <MswRouteListTable
      headerMode="only"
      jobs={jobs}
      selectedIds={selected}
      onSelectionChange={(next) =>
        splitCheckedJobs(routes, next).forEach(([routeId, ids]) => onSelectionChange(routeId, ids))
      }
      showRemoveColumn={withRemove}
      {...rest}
    />
  );
};
